import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { MasterDataService } from '../../../core/services/masterData/master-data.service';

interface MasterDataClientModel {
  id?: string;
  documentId?: string;

  email?: string;
  firstName?: string;
  lastName?: string;
  document?: string;
  phone?: string;
  isNewsletterOptIn?: boolean;

  /**
   * Campo exemplo para edição.
   * Troque pelo campo real da entidade CL que você deseja alterar.
   */
  approved?: boolean;

  [key: string]: any;
}

@Component({
  selector: 'app-master-data',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, LoadingComponent, AlertComponent],
  templateUrl: './master-data.component.html',
  styleUrl: './master-data.component.scss',
})
export class MasterDataComponent {
  email = '';

  client: MasterDataClientModel | null = null;
  originalClient: MasterDataClientModel | null = null;

  /**
   * Valor do campo que será editado.
   * Neste exemplo, o campo é boolean.
   */
  editableFieldValue = '';

  loading = false;
  saving = false;

  error: string | null = null;
  success: string | null = null;

  constructor(private readonly masterDataService: MasterDataService) {}

  searchByEmail(): void {
    const email = this.email.trim();

    this.clearMessages();
    this.client = null;
    this.originalClient = null;
    this.editableFieldValue = '';

    if (!email) {
      this.error = 'Informe um e-mail para realizar a busca.';
      return;
    }

    this.loading = true;

    this.masterDataService.getClientByEmail(email).subscribe({
      next: (clients: MasterDataClientModel[]) => {
        this.loading = false;

        if (!clients || clients.length === 0) {
          this.error = 'Nenhum cliente encontrado para o e-mail informado.';
          return;
        }

        this.client = clients[0];
        this.originalClient = structuredClone(clients[0]);

        this.editableFieldValue = this.client.document || '';
      },
      error: (err: any) => {
        this.loading = false;
        this.error = 'Falha ao buscar dados no Master Data.';
        console.error('Erro ao buscar cliente no Master Data:', err);
      },
    });
  }

  saveEditableField(): void {
    this.clearMessages();

    if (!this.client) {
      this.error = 'Nenhum cliente carregado para edição.';
      return;
    }

    const documentId = this.getDocumentId();

    if (!documentId) {
      this.error = 'Não foi possível identificar o ID do documento no Master Data.';
      return;
    }

    const documentValue = this.editableFieldValue.trim();

    if (!documentValue) {
      this.error = 'Informe um documento válido.';
      return;
    }

    this.saving = true;

    const payload: Partial<MasterDataClientModel> = {
      document: documentValue,
    };

    this.masterDataService.updateClientField(documentId, payload).subscribe({
      next: () => {
        this.saving = false;
        this.success = 'Documento atualizado com sucesso.';

        this.client = {
          ...this.client,
          ...payload,
        };

        this.originalClient = structuredClone(this.client);
      },
      error: (err: any) => {
        this.saving = false;
        this.error = 'Falha ao salvar documento no Master Data.';
        console.error('Erro ao atualizar documento no Master Data:', err);
      },
    });
  }

  hasChanges(): boolean {
    if (!this.client) {
      return false;
    }

    return this.editableFieldValue !== this.client.document;
  }

  getDocumentId(): string | null {
    if (!this.client) {
      return null;
    }

    return this.client.id || this.client.documentId || null;
  }

  getClientFields(): Array<{ key: string; value: any }> {
    if (!this.client) {
      return [];
    }

    return Object.entries(this.client).map(([key, value]) => ({
      key,
      value,
    }));
  }

  formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }

    if (Array.isArray(value)) {
      return value.length ? JSON.stringify(value) : '-';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  private clearMessages(): void {
    this.error = null;
    this.success = null;
  }
}
